import { Injectable } from '@angular/core';
import { CustomOrderEntry } from '@cart/const';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CartActions, CustomActiveCartService } from '@cart/data-access';
import { CustomBaseStore } from '@city/const';
import { CityService } from '@city/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_REQUEST, RouterRequestAction } from '@ngrx/router-store';
import { SelectedPrice } from '@product/const';
import { CustomProductActions, SelectedPriceService } from '@product/data-access';
import { ManualPriceService } from '@product/feature';
import { Observable, ReplaySubject } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class ManualPriceAnalyticsEffects {
  isInitialized$ = new ReplaySubject(1);

  onSetManualPriceAndLeftPage$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_REQUEST),
        switchMap((action) => this.isInitialized$.pipe(map(() => action))),
        map((action: RouterRequestAction<any>) => action.payload),
        filter((payload) => payload.routerState.semanticRoute === 'product'),
        concatLatestFrom((payload) => [
          this.selectedPriceService.getSelectedPrice(payload.routerState.context.id),
          this.cityService.activeBaseStore$,
          this.customActiveCartService.getEntry(payload.routerState.context.id),
        ]),
        filter(([, , , entry]: [any, SelectedPrice, CustomBaseStore, CustomOrderEntry]) => !entry),
        concatLatestFrom(([payload, price, baseStore]: [any, SelectedPrice, CustomBaseStore, CustomOrderEntry]) => {
          const key = this.manualPriceService.keyGenerator(
            price.unit.code,
            payload.routerState.context.id,
            baseStore.code,
          );

          return this.manualPriceService.getPriceItem(key);
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  onSetManualPriceAndAddToCart$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.CART_ADD_ENTRY),
        switchMap((action) => this.isInitialized$.pipe(map(() => action))),
        concatLatestFrom((action: CartActions.CartAddEntry) => [
          this.selectedPriceService.getSelectedPrice(action.payload.productCode),
          this.cityService.activeBaseStore$,
        ]),
        concatLatestFrom(([action, price, baseStore]: [CartActions.CartAddEntry, SelectedPrice, CustomBaseStore]) => {
          const key = this.manualPriceService.keyGenerator(price.unit.code, action.payload.productCode, baseStore.code);

          return this.manualPriceService.getPriceItem(key);
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  onViewProductCardWithManualPrice$: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CustomProductActions.PRODUCT_CARD_MANUAL_PRICE_INIT),
        tap(() => {
          this.isInitialized$.next(true);
        }),
        map(() => undefined),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private manualPriceService: ManualPriceService,
    private selectedPriceService: SelectedPriceService,
    private cityService: CityService,
    private customActiveCartService: CustomActiveCartService,
  ) {}
}
